/**
 * 设备检测和响应式工具函数
 * 提供设备类型检测、viewport信息获取等功能
 */

// 设备信息接口
export interface DeviceInfo {
  type: 'mobile' | 'tablet' | 'desktop';
  screenWidth: number;
  screenHeight: number;
  orientation: 'portrait' | 'landscape';
  touchSupport: boolean;
  userAgent: string;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

// Viewport信息接口
export interface ViewportInfo {
  width: number;
  height: number;
  breakpoint: 'mobile' | 'tablet' | 'desktop' | '2xl';
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

// 断点配置 - 根据新的适配需求调整
const BREAKPOINTS = {
  mobile: 479,    // 0-479px (移动端)
  tablet: 480,    // 480px+ (平板端开始)
  desktop: 1264,  // 1264px+ (桌面端开始)
  '2xl': 1536,    // 1536px+ (大屏桌面)
} as const;

/**
 * 获取当前设备信息
 */
export function getDeviceInfo(): DeviceInfo {
  const userAgent = typeof window !== 'undefined' ? navigator.userAgent : '';
  const screenWidth = typeof window !== 'undefined' ? window.screen.width : 0;
  const screenHeight = typeof window !== 'undefined' ? window.screen.height : 0;
  
  // 检测触摸支持
  const touchSupport = typeof window !== 'undefined' && (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore
    navigator.msMaxTouchPoints > 0
  );

  // 检测设备方向
  const orientation = screenWidth > screenHeight ? 'landscape' : 'portrait';

  // 设备类型检测
  let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  
  if (typeof window !== 'undefined') {
    const width = window.innerWidth;
    
    if (width <= BREAKPOINTS.mobile) {
      deviceType = 'mobile';
    } else if (width < BREAKPOINTS.desktop && touchSupport) {
      deviceType = 'tablet';
    } else {
      deviceType = 'desktop';
    }
  }

  return {
    type: deviceType,
    screenWidth,
    screenHeight,
    orientation,
    touchSupport,
    userAgent,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
  };
}

/**
 * 获取当前viewport信息
 */
export function getViewportInfo(): ViewportInfo {
  const width = typeof window !== 'undefined' ? window.innerWidth : 0;
  const height = typeof window !== 'undefined' ? window.innerHeight : 0;

  // 确定当前断点
  let breakpoint: ViewportInfo['breakpoint'] = 'mobile';
  if (width >= BREAKPOINTS['2xl']) {
    breakpoint = '2xl';
  } else if (width >= BREAKPOINTS.desktop) {
    breakpoint = 'desktop';
  } else if (width >= BREAKPOINTS.tablet) {
    breakpoint = 'tablet';
  } else {
    breakpoint = 'mobile';
  }

  return {
    width,
    height,
    breakpoint,
    isMobile: width <= BREAKPOINTS.mobile,
    isTablet: width > BREAKPOINTS.mobile && width < BREAKPOINTS.desktop,
    isDesktop: width >= BREAKPOINTS.desktop,
  };
}

/**
 * 检查是否为移动设备
 */
export function isMobileDevice(): boolean {
  return getDeviceInfo().isMobile;
}

/**
 * 检查是否为平板设备
 */
export function isTabletDevice(): boolean {
  return getDeviceInfo().isTablet;
}

/**
 * 检查是否为桌面设备
 */
export function isDesktopDevice(): boolean {
  return getDeviceInfo().isDesktop;
}

/**
 * 检查是否支持触摸
 */
export function isTouchDevice(): boolean {
  return getDeviceInfo().touchSupport;
}

/**
 * 获取当前断点
 */
export function getCurrentBreakpoint(): ViewportInfo['breakpoint'] {
  return getViewportInfo().breakpoint;
}

/**
 * 检查当前是否匹配指定断点
 */
export function matchesBreakpoint(breakpoint: ViewportInfo['breakpoint']): boolean {
  return getCurrentBreakpoint() === breakpoint;
}

/**
 * 检查当前宽度是否大于等于指定断点
 */
export function isBreakpointUp(breakpoint: keyof typeof BREAKPOINTS): boolean {
  const width = typeof window !== 'undefined' ? window.innerWidth : 0;
  return width >= BREAKPOINTS[breakpoint];
}

/**
 * 检查当前宽度是否小于指定断点
 */
export function isBreakpointDown(breakpoint: keyof typeof BREAKPOINTS): boolean {
  const width = typeof window !== 'undefined' ? window.innerWidth : 0;
  return width < BREAKPOINTS[breakpoint];
}

/**
 * 防抖函数
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * 节流函数
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}